/**
 * Rowted's own people, on the shared list engine — `rowted-admin-v3.md` §3.1,
 * board 4.
 *
 * This was a hand-rolled grid: a header row, a body row, and the two sized
 * independently so nothing lined up. v2's note beside it argued that five
 * people do not earn an `AreaDef`, and that was the wrong test. The question
 * the engine answers is not "are there many rows" but "are there questions
 * nobody anticipated" — *who has not set up two-step*, *who was invited and
 * never came*, *who has been inside a client this month* — and a screen that
 * draws five rows can answer none of them.
 *
 * So the list is `ListScreen` over `PLATFORM_ADMINS_AREA`, and what left the
 * page with the grid was the two cards beside it: the role matrix is now a
 * read-only flyout behind **Roles**, and the security switches live under
 * **Settings › Admin access**, where the things they change are saved. A
 * switch that saved from two screens is two places to get the confirmation
 * wrong.
 *
 * **`admins` is an owner's ability and nobody else's** (§2.3). Every role may
 * read this list — a support account that cannot see who the owners are cannot
 * tell who to ask — and every action is absent rather than disabled, because
 * `shared/src/platform-roles.ts` is the same table the server gates on.
 */
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ApiError, PLATFORM_ROLES, PLATFORM_ROLE_BLURBS, PLATFORM_ROLE_LABELS,
  type PlatformRole,
} from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import { Avatar, Button, Tag, type TagTone } from '../../components/primitives.js'
import { Field, SelectField } from '../../components/fields.js'
import { Sheet } from '../../components/sheet.js'
import { Modal, Toast } from '../../components/overlays.js'
import { ListScreen } from '../../components/list-screen.js'
import { useList, type ListRow } from '../../lib/use-list.js'
import { formatRelative } from '../../lib/format.js'
import { IconAdminUsers, IconPlus, IconSpinner, IconWarn } from '../../frame/icons.js'
import { InviteLink, type Delivered } from './invite-link.js'
import { AdminPage, can, useAdminUser } from './shell.js'
import { useStepUp } from './step-up.js'

/** What `GET /admins/invite-readiness` answers — the two conditions on sending. */
interface Readiness {
  domains: string[]
  mail: { ready: boolean; reason?: string }
}

const STATUS: Record<string, { label: string; tone: TagTone }> = {
  active: { label: 'Active', tone: 'ok' },
  invited: { label: 'Invited', tone: 'warn' },
  suspended: { label: 'Suspended', tone: 'bad' },
}

/** What a row is called, for the sentences an action says back. */
const nameOf = (row: ListRow): string => String(row.name ?? 'that admin')

export function AdminAdmins() {
  const { me } = useAdminUser()
  const navigate = useNavigate()
  const stepUp = useStepUp()

  const [note, setNote] = useState<{ text: string; tone: 'default' | 'danger' } | null>(null)
  const [inviting, setInviting] = useState(false)
  const [showingRoles, setShowingRoles] = useState(false)
  const [changingRole, setChangingRole] = useState<ListRow | null>(null)
  const [ready, setReady] = useState<Readiness | null>(null)
  /* The link, shown only when the email did not go — see `InviteLink`. */
  const [undelivered, setUndelivered] = useState<Delivered | null>(null)

  const list = useList('admins', {
    /* The row menu needs these whether or not the columns are shown. */
    alwaysFetch: ['status', 'email', 'role'],
    request: adminRequest,
  })

  /**
   * The flyout needs the two conditions on sending an invite — which domains
   * are allowed, and whether there is a mailbox to send through — and neither
   * is part of this list.
   *
   * One request, when the flyout opens rather than with the page. It used to
   * ask `/settings` for the domains alone, which meant the *other* condition
   * was discovered by filling the form in, spending a step-up, and being told
   * the invite was sent when it was not.
   */
  const openInvite = useCallback(() => {
    setInviting(true)
    setReady(null)
    void adminRequest<Readiness>('/admins/invite-readiness')
      .then(setReady)
      /* Without it the flyout works and simply says less. */
      .catch(() => setReady({ domains: [], mail: { ready: true } }))
  }, [])

  /**
   * Sending an invite, which is the one action on this page whose **answer**
   * matters rather than just its success.
   *
   * `act` toasts a sentence it was handed before the request went out, which
   * is right for a role change and wrong here: whether an email reached
   * somebody is not knowable in advance, and the version of this that assumed
   * it said "Invite sent" into a deployment with no mailbox.
   */
  const invite = useCallback((path: string, body: Record<string, unknown>, email: string) => {
    setNote(null)
    /*
     * The *action* decides what to say, not the `.then` — `guard` resolves a
     * boolean (it ran, or the code dialog was closed) and never the body, and
     * it replays the whole action after a code, so reading the answer in here
     * is what makes the second attempt report itself like the first.
     */
    void stepUp.guard(async () => {
      const answer = await adminRequest<Delivered>(path, { method: 'POST', body })
      void list.reload()
      if (answer.delivered) {
        setNote({ text: `Invite sent to ${email} · expires in 3 days.`, tone: 'default' })
      } else if (answer.link) {
        setUndelivered({ ...answer, email })
      } else {
        /* Undelivered and no link: nothing to hand over, so say the plain
           thing rather than opening an empty dialog. */
        setNote({
          text: answer.reason ?? `The invite for ${email} was made, but the email did `
            + 'not send. Use Resend invite once the mailbox is connected.',
          tone: 'danger',
        })
      }
    }).catch((caught: unknown) => setNote({
      text: caught instanceof ApiError ? caught.message : 'That did not work.',
      tone: 'danger',
    }))
  }, [list, stepUp])

  /** Every action on this screen: a step-up, then a reload, then a sentence. */
  const act = useCallback((
    path: string, body: Record<string, unknown>, said: string,
    method: 'POST' | 'PATCH' = 'POST',
  ) => {
    setNote(null)
    void stepUp.guard(async () => { await adminRequest(path, { method, body }) })
      .then((ran) => {
        if (!ran) return
        setNote({ text: said, tone: 'default' })
        setChangingRole(null)
        void list.reload()
      })
      .catch((caught: unknown) => setNote({
        text: caught instanceof ApiError ? caught.message : 'That did not work.',
        tone: 'danger',
      }))
  }, [list, stepUp])

  const mayManage = can(me, 'admins')

  return (
    <AdminPage me={me} active="admins">
      <ListScreen
        area="admins"
        title="Admin users"
        chrome="page"
        cards
        list={list}
        request={adminRequest}
        primaryColumn={{
          field: 'name',
          header: 'Person',
          render: (row: ListRow) => ({
            primary: nameOf(row),
            secondary: String(row.email ?? '') || undefined,
            mark: <Avatar name={nameOf(row)} size={28} />,
          }),
        }}
        cell={{
          role: (row) => PLATFORM_ROLE_LABELS[row.role as PlatformRole] ?? String(row.role ?? ''),
          status: (row) => {
            const status = STATUS[String(row.status ?? '')]
            return status ? <Tag tone={status.tone}>{status.label}</Tag> : null
          },
          /* "Not enrolled" is the answer this column exists for (§6.12). */
          mfa: (row) => (row.mfa === 'none'
            ? <span className="text-warn">Not enrolled</span>
            : 'Authenticator'),
          lastSignInAt: (row) => (row.lastSignInAt
            ? formatRelative(String(row.lastSignInAt))
            : <span className="text-text-faint">never</span>),
        }}
        onRowClick={(row) => navigate(`/admin/admins/${String(row.id)}`)}
        /*
         * `ListScreen` has no `newAction`: the page bar's buttons are one
         * `actions` node, primary last, the way every other list screen in the
         * app draws them. It had one until the browser showed no Invite button
         * on a page whose owner may invite — a spread of an object literal into
         * JSX takes no excess-property check, so the prop was accepted by the
         * compiler and dropped by the component.
         */
        actions={(
          <>
            <Button onClick={() => setShowingRoles(true)}>Roles</Button>
            {mayManage && (
              <Button variant="primary" onClick={openInvite}>
                <IconPlus size={15} />
                Invite admin
              </Button>
            )}
          </>
        )}
        rowMenu={(row) => [
          {
            key: 'open',
            label: 'Open',
            onSelect: () => navigate(`/admin/admins/${String(row.id)}`),
          },
          ...(mayManage
            ? [
                {
                  key: 'role',
                  label: 'Change role…',
                  onSelect: () => setChangingRole(row),
                },
                ...(row.status === 'invited'
                  ? [{
                      key: 'resend',
                      label: 'Resend invite',
                      separatorBefore: true,
                      /* The same answer as a first invite, so the same path. */
                      onSelect: () => invite(
                        `/admins/${String(row.id)}/resend-invite`, {},
                        String(row.email ?? 'them')),
                    }]
                  : []),
                {
                  key: 'revoke',
                  label: 'Revoke sessions',
                  separatorBefore: row.status !== 'invited',
                  onSelect: () => act(
                    `/admins/${String(row.id)}/revoke-sessions`, {},
                    `${nameOf(row)} is signed out everywhere.`),
                },
                /*
                 * Suspend, not remove. Rule 2 and §2.3: an admin who has
                 * signed in as a client is on every one of those records for
                 * ever, so their account stays and stops working. Remove
                 * exists on the API for an invite nobody accepted; it is not
                 * offered on a row that has ever signed in.
                 */
                ...(row.status === 'suspended'
                  ? [{
                      key: 'restore',
                      label: 'Restore access',
                      tone: 'primary' as const,
                      onSelect: () => act(
                        `/admins/${String(row.id)}`, { status: 'active' },
                        `${nameOf(row)} can sign in again.`, 'PATCH'),
                    }]
                  : [{
                      key: 'suspend',
                      label: 'Suspend',
                      tone: 'danger' as const,
                      onSelect: () => act(
                        `/admins/${String(row.id)}`, { status: 'suspended' },
                        `${nameOf(row)} can no longer sign in.`, 'PATCH'),
                    }]),
              ]
            : []),
        ]}
        emptyTitle="Nobody yet"
        emptyBody="bootstrap:platform makes the first owner; every other admin is invited."
      />

      {inviting && (
        <InviteSheet
          ready={ready}
          onClose={() => setInviting(false)}
          onInvite={(body, email) => {
            invite('/admins/invite', body, email)
            setInviting(false)
          }}
        />
      )}

      {undelivered?.link && (
        <InviteLink
          answer={{ ...undelivered, link: undelivered.link }}
          onClose={() => setUndelivered(null)}
        />
      )}

      {showingRoles && <RolesFlyout onClose={() => setShowingRoles(false)} />}

      {changingRole && (
        <RoleDialog
          admin={{
            name: nameOf(changingRole),
            role: changingRole.role as PlatformRole,
          }}
          onClose={() => setChangingRole(null)}
          onChange={(role) => act(
            `/admins/${String(changingRole.id)}`, { role },
            `${nameOf(changingRole)} is now ${PLATFORM_ROLE_LABELS[role]}.`, 'PATCH')}
        />
      )}

      {note && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Toast>
        </div>
      )}

      {stepUp.dialog}
    </AdminPage>
  )
}

/**
 * What each role can do, read-only — the card that used to sit beside the
 * grid, now behind **Roles** on the page bar.
 *
 * Still on the screen rather than only in the docs, because the question this
 * page exists to answer is never "who is Priya", it is "should Priya be an
 * admin" — and that cannot be answered without knowing what admin means. What
 * changed is that it no longer costs a third of the width to somebody who
 * already knows.
 */
function RolesFlyout({ onClose }: { onClose: () => void }) {
  return (
    <Sheet
      open
      onClose={onClose}
      kind="contact"
      title="What each role can do"
      sub="The same table the server checks"
      width={480}
      note="Set on a person from their row's Change role."
      primary={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      <ul className="m-0 list-none p-0">
        {PLATFORM_ROLES.map((role) => (
          <li key={role} className="border-b border-line-soft px-5 py-3 last:border-b-0">
            <b className="text-cell text-text">{PLATFORM_ROLE_LABELS[role]}</b>
            <p className="m-0 mt-0.5 text-[11.5px] leading-[1.45] text-text-muted">
              {PLATFORM_ROLE_BLURBS[role]}
              {role === 'owner' && ' At least one owner always exists.'}
            </p>
          </li>
        ))}
      </ul>
    </Sheet>
  )
}

/*
 * `Security` and `SecurityRow` moved to **Settings › Admin access**.
 *
 * They were read-only here and saved there, which is the shape of a thing in
 * the wrong place: the switches now sit with the settings they describe, and
 * this page is the people.
 */

function InviteSheet({ ready, onClose, onInvite }: {
  /** `null` while `/admins/invite-readiness` is out. */
  ready: Readiness | null
  onClose: () => void
  onInvite: (body: Record<string, unknown>, email: string) => void
}) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState<PlatformRole>('admin')
  const [busy, setBusy] = useState(false)

  const filled = email.includes('@') && firstName.trim() !== '' && lastName.trim() !== ''
  const offered = PLATFORM_ROLES.filter((one) => one !== 'owner')
  const domains = ready?.domains ?? []
  /* Unknown reads as working: the strip is a warning, not a guess. */
  const noMailbox = ready !== null && !ready.mail.ready

  const send = () => {
    setBusy(true)
    onInvite({
      email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), role,
    }, email.trim())
  }

  return (
    <Sheet
      open
      onClose={onClose}
      /*
       * `contact` for the type's sake and a `mark` over it: there is no
       * `admin` in `RECORD_ICONS`, and adding one would put a Rowted employee
       * in the table the nav rail, the lookups and every builder row read
       * from — the same reason step 5 gave `Sheet` a mark for a vendor.
       */
      kind="contact"
      mark={(
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-tile
                         bg-surface-alt text-text-muted">
          <IconAdminUsers size={20} />
        </span>
      )}
      title="Invite an admin"
      sub="Admin users · Rowted platform"
      width={620}
      note={<span className="text-[11.5px] text-text-faint">Owners only · logged</span>}
      primary={(
        <Button variant="primary" onClick={send} disabled={!filled || busy}>
          {busy ? <IconSpinner size={15} /> : null}
          {/* What the button will actually do, which is the whole point of
              knowing before the form is filled in. */}
          {noMailbox ? 'Create invite link' : 'Send invite'}
        </Button>
      )}
    >
      <div className="flex flex-col gap-4 p-4">
        {/*
          * Said before the form rather than after the step-up. There is still
          * a way in when no mailbox is connected — the link comes back on this
          * screen — and the first owner is bootstrapped from the command line
          * precisely because the second is invited before anybody has had a
          * chance to connect Microsoft 365.
          */}
        {noMailbox && (
          <p className="m-0 flex items-start gap-2 rounded-field bg-warn-bg px-3 py-2.5
                        text-[11.5px] leading-[1.45] text-warn">
            <span className="mt-px shrink-0"><IconWarn size={14} /></span>
            <span>
              <b className="font-semibold">No mailbox is connected, so nothing will be
              emailed.</b>{' '}
              The invite link comes back on this screen instead — send it to them yourself.
              {ready?.mail.reason ? ` ${ready.mail.reason}` : ''}
            </span>
          </p>
        )}

        <Field
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          required
          autoFocus
          hint={domains.length
            ? `Must be ${domains.join(' or ')}. The invite is a one-time link that works `
              + 'for 3 days.'
            : 'The invite is a one-time link that works for 3 days.'}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" value={firstName} onChange={setFirstName} required />
          <Field label="Last name" value={lastName} onChange={setLastName} required />
        </div>

        {/*
          * A radio list rather than a dropdown: choosing what somebody may do
          * inside every customer is not a choice to make from four words in a
          * closed box, and each line's consequence has to be on screen while
          * it is being made.
          */}
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-0.5 p-0 text-cell font-medium text-text">Role</legend>
          {offered.map((one) => (
            <label
              key={one}
              className={`flex cursor-pointer items-start gap-2.5 rounded-field border px-3 py-2
                ${role === one
                  ? 'border-accent-line bg-accent-soft'
                  : 'border-line bg-surface hover:bg-surface-alt'}`}
            >
              <input
                type="radio"
                name="role"
                checked={role === one}
                onChange={() => setRole(one)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-accent"
              />
              <span className="min-w-0">
                <span className="block text-cell font-medium text-text">
                  {PLATFORM_ROLE_LABELS[one]}
                </span>
                <span className="block text-[11.5px] leading-[1.45] text-text-muted">
                  {PLATFORM_ROLE_BLURBS[one]}
                </span>
              </span>
            </label>
          ))}
          <p className="m-0 mt-0.5 text-[11.5px] leading-[1.45] text-text-faint">
            Owner is not offered here. An owner is promoted after they have an account, so
            nobody becomes one through a link in an email.
          </p>
        </fieldset>

        <div className="rounded-field bg-surface-alt p-3">
          <b className="text-cell text-text">
            What {firstName.trim() || 'they'} get{firstName.trim() ? 's' : ''}
          </b>
          <p className="m-0 mt-1 text-[11.5px] leading-[1.45] text-text-muted">
            {noMailbox ? 'A link you send them' : 'An email with a link'} → set a password →
            enrol an authenticator → ten recovery codes, shown once → in. The link is spent
            on first use; you can resend or suspend it from this page.
          </p>
        </div>
      </div>
    </Sheet>
  )
}

function RoleDialog({ admin, onClose, onChange }: {
  admin: { name: string; role: PlatformRole }
  onClose: () => void
  onChange: (role: PlatformRole) => void
}) {
  const [role, setRole] = useState<PlatformRole>(admin.role)
  return (
    <Modal
      open
      onClose={onClose}
      title={`Change ${admin.name.split(' ')[0]}’s role`}
      width={460}
      footerNote="Recorded in the audit log. Asking for your code again."
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => onChange(role)}
            disabled={role === admin.role}
          >
            Change role
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <SelectField
          label="Role"
          value={role}
          onChange={(next) => setRole(next as PlatformRole)}
          hint={PLATFORM_ROLE_BLURBS[role]}
        >
          {PLATFORM_ROLES.map((one) => (
            <option key={one} value={one}>{PLATFORM_ROLE_LABELS[one]}</option>
          ))}
        </SelectField>
        {/*
          * Owner *is* offered here, unlike on the invite — promotion is the
          * deliberate act §5.3 asks for, on somebody who already has an
          * account and has enrolled two-step.
          */}
        {role === 'owner' && admin.role !== 'owner' && (
          <p className="m-0 rounded-field bg-warn-bg px-3 py-2 text-[11.5px] leading-[1.45]
                        text-warn">
            An owner can read and change every credential Rowted holds, and can invite and
            remove other admins — including you.
          </p>
        )}
        {admin.role === 'owner' && role !== 'owner' && (
          <p className="m-0 rounded-field bg-warn-bg px-3 py-2 text-[11.5px] leading-[1.45]
                        text-warn">
            Demoting the last owner is refused by the database, not by this screen — so if
            they are the only one, this will come back with that message.
          </p>
        )}
      </div>
    </Modal>
  )
}

/**
 * The ⋯ on a row, opening the app's one menu.
 *
 * `ContextMenu` positions at a point rather than against an element, because
 * it is the same menu a right-click opens — so this hands it the button's own
 * corner. A second menu component would drift from that one on padding,
 * dismissal and how a destructive item is coloured.
 */

/*
 * `MoreButton` went with the hand-rolled grid. `ListScreen`'s `rowMenu` draws
 * the three dots on every list in the product, which is the point of it.
 */
